export interface AnalysisData {
  bass: number;
  mid: number;
  high: number;
  average: number;
}

export class AudioAnalyzer {
  private analyser: AnalyserNode;
  private dataArray: Uint8Array;
  private bufferLength: number;
  
  // Smoothed values for more organic reactivity
  private smoothedBass: number = 0;
  private smoothedMid: number = 0;
  private smoothedHigh: number = 0;
  private smoothedAverage: number = 0;
  
  private smoothingFactor: number = 0.15;
  
  constructor(analyser: AnalyserNode) {
    this.analyser = analyser;
    this.bufferLength = analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(this.bufferLength);
  }
  
  public analyze(): AnalysisData {
    this.analyser.getByteFrequencyData(this.dataArray);
    
    // Divide frequency spectrum into bass, mid, high
    const bassEnd = Math.floor(this.bufferLength * 0.15);
    const midEnd = Math.floor(this.bufferLength * 0.5);
    
    let bassSum = 0;
    let midSum = 0;
    let highSum = 0;
    let totalSum = 0;
    
    for (let i = 0; i < this.bufferLength; i++) {
      const value = this.dataArray[i];
      totalSum += value;
      
      if (i < bassEnd) {
        bassSum += value;
      } else if (i < midEnd) {
        midSum += value;
      } else {
        highSum += value;
      }
    }
    
    // Normalize to 0-1 range
    const bassRaw = bassSum / (bassEnd * 255);
    const midRaw = midSum / ((midEnd - bassEnd) * 255);
    const highRaw = highSum / ((this.bufferLength - midEnd) * 255);
    const averageRaw = totalSum / (this.bufferLength * 255);
    
    // Apply smoothing (exponential moving average)
    this.smoothedBass = this.lerp(this.smoothedBass, bassRaw, this.smoothingFactor);
    this.smoothedMid = this.lerp(this.smoothedMid, midRaw, this.smoothingFactor);
    this.smoothedHigh = this.lerp(this.smoothedHigh, highRaw, this.smoothingFactor);
    this.smoothedAverage = this.lerp(this.smoothedAverage, averageRaw, this.smoothingFactor);
    
    return {
      bass: this.smoothedBass,
      mid: this.smoothedMid,
      high: this.smoothedHigh,
      average: this.smoothedAverage
    };
  }
  
  private lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
  }
  
  public setSmoothingFactor(factor: number): void {
    this.smoothingFactor = Math.max(0.01, Math.min(1, factor));
  }
}

